/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Create a journey", description = "Choose who enters, add journey tiles, set exit criteria and a schedule, then test and activate the journey.", lastUpdatedDate = new Date(1793664000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Create a journey",
        description: "Choose who enters, add journey tiles, set exit criteria and a schedule, then test and activate the journey.",
        lastUpdatedDate: "Tue Nov 03 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Before you create a journey",
          "depth": 0,
          "id": "before-you-create-a-journey"
        }, {
          "value": "1. Create the journey",
          "depth": 0,
          "id": "1-create-the-journey"
        }, {
          "value": "2. Choose who enters",
          "depth": 0,
          "id": "2-choose-who-enters"
        }, {
          "value": "Use an audience for entry",
          "depth": 1,
          "id": "use-an-audience-for-entry"
        }, {
          "value": "Use an event for entry",
          "depth": 1,
          "id": "use-an-event-for-entry"
        }, {
          "value": "Configure entry behavior",
          "depth": 1,
          "id": "configure-entry-behavior"
        }, {
          "value": "3. Add journey tiles",
          "depth": 0,
          "id": "3-add-journey-tiles"
        }, {
          "value": "Add journey notes",
          "depth": 1,
          "id": "add-journey-notes"
        }, {
          "value": "4. Set exit criteria",
          "depth": 0,
          "id": "4-set-exit-criteria"
        }, {
          "value": "5. Set the schedule",
          "depth": 0,
          "id": "5-set-the-schedule"
        }, {
          "value": "6. Test and activate",
          "depth": 0,
          "id": "6-test-and-activate"
        }, {
          "value": "Examples",
          "depth": 0,
          "id": "examples"
        }, {
          "value": "Welcome series",
          "depth": 1,
          "id": "welcome-series"
        }, {
          "value": "Cart abandonment",
          "depth": 1,
          "id": "cart-abandonment"
        }, {
          "value": "Re-engagement",
          "depth": 1,
          "id": "re-engagement"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and operations teams creating a journey"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A defined ", _jsx(_components.a, {
                    href: "/customer-studio/schema",
                    children: "schema"
                  }), " with a parent model on the ", _jsx(_components.a, {
                    href: "/syncs/lightning-sync-engine",
                    children: "Lightning sync engine"
                  })]
                }), _jsxs("li", {
                  children: ["An ", _jsx(_components.a, {
                    href: "/customer-studio/usage",
                    children: "audience"
                  }), " or event to use as the journey entry condition"]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Create a journey by choosing who enters, adding the tiles members move through, setting exit criteria and a schedule, then testing and activating it."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
        children: ["For details about each available tile, see ", _jsx(_components.a, {
          href: "/customer-studio/journeys/tiles",
          children: "Journey tiles"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "before-you-create-a-journey",
        children: "Before you create a journey"
      }), "\n", _jsx(_components.p, {
        children: "Before you configure the journey, decide:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "What should trigger entry?"
          }), " Members can enter when they join an audience or perform an event."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "What should happen after entry?"
          }), " Decide where members should wait, branch, receive messages, or sync to a destination."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "What should cause a member to exit?"
          }), " Define the conditions that should remove them before they reach the end."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "How precise does the timing need to be?"
          }), " This determines how frequently the journey should run."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you only need to activate an audience in a destination without delays or branching, ", _jsx(_components.a, {
          href: "/customer-studio/syncs",
          children: "sync the audience"
        }), " directly instead."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Journeys identify and de-duplicate members by the parent model's ", _jsx(_components.a, {
            href: "/customer-studio/data-model",
            children: "primary\nkey"
          }), ". Use a stable, unique primary key: if the\nkey isn't unique, or if it changes for a member (for example, when identity\nresolution reassigns it), a journey can re-enroll members or send duplicate\nmessages."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "1-create-the-journey",
        children: "1. Create the journey"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsxs(_components.strong, {
            children: ["Customer Studio > ", _jsx(_components.a, {
              href: "https://app.hightouch.com/journeys?from=docs",
              children: "Journeys"
            })]
          }), ".\n", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/cs-journey-list.png",
            alt: "Journey list view"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add journey"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a ", _jsx(_components.strong, {
            children: "parent model"
          }), ", enter a ", _jsx(_components.strong, {
            children: "journey name"
          }), ", and optionally add a ", _jsx(_components.strong, {
            children: "description"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create journey"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The journey canvas opens in ", _jsx(_components.strong, {
          children: "Draft"
        }), " mode."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-create-modal.png",
          alt: "Create journey modal"
        })
      }), "\n", _jsx(_components.h2, {
        id: "2-choose-who-enters",
        children: "2. Choose who enters"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Start"
        }), " tile defines how members enter the journey. Select it, then choose the entry type that matches the condition you want to use:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Entry type"
            }), _jsx(_components.th, {
              children: "Use when"
            }), _jsx(_components.th, {
              children: "Example"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Enters audience"
              })
            }), _jsx(_components.td, {
              children: "Entry is based on a current state"
            }), _jsx(_components.td, {
              children: "Members who haven't purchased in 30 days"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Performs events"
              })
            }), _jsx(_components.td, {
              children: "Entry is based on an action with a timestamp"
            }), _jsx(_components.td, {
              children: "Members who abandoned a cart"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Use audience entry when qualification is based on a state. Use event entry when the journey should start from a specific action and time, such as abandoning a cart or creating an account."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "You can't change the entry type after the journey's first run. Confirm that it\nrepresents the intended trigger before you activate the journey."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journey-start-config.png",
          alt: "Start tile configuration"
        })
      }), "\n", _jsx(_components.h3, {
        id: "use-an-audience-for-entry",
        children: "Use an audience for entry"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select an ", _jsx(_components.strong, {
            children: "entry audience"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Configure ", _jsx(_components.a, {
            href: "#configure-entry-behavior",
            children: "re-entry and exclusions"
          }), ", if needed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Update"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journey-start-audience.png",
          alt: "Audience-based start configuration"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "On the first run after activation, an audience journey enrolls everyone\ncurrently in the entry audience. On later runs, it enrolls members as they\nnewly join the audience."
        })
      }), "\n", _jsx(_components.h3, {
        id: "use-an-event-for-entry",
        children: "Use an event for entry"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "event"
          }), " that triggers entry."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Only events with assigned primary keys can enter a journey. Events without a\nprimary key are unavailable for selection."
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Optional: Enable ", _jsx(_components.strong, {
            children: "Include past events"
          }), " and set a lookback window. When disabled, only events that occur after the journey starts qualify."]
        }), "\n", _jsxs(_components.li, {
          children: ["If the parent model has subset categories, select a subset in each required category under ", _jsx(_components.strong, {
            children: "Subsets"
          }), ". See ", _jsx(_components.a, {
            href: "#subsets",
            children: "Subsets"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Configure ", _jsx(_components.a, {
            href: "#configure-entry-behavior",
            children: "re-entry and exclusions"
          }), ", if needed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Update"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journey-start-event.png",
          alt: "Event-based start configuration"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-entry-behavior",
        children: "Configure entry behavior"
      }), "\n", _jsx(_components.p, {
        children: "The default entry settings allow each member to enter once and don't exclude members who are already in other journeys. Change them when your journey needs different behavior."
      }), "\n", _jsx(_components.h4, {
        id: "re-entry",
        children: "Re-entry"
      }), "\n", _jsx(_components.p, {
        children: "Re-entry controls how many times a member can enter:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Only one entry"
          }), " ", _jsx(_components.em, {
            children: "(default)"
          }), " — The member can enter once."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Specific number of entries"
          }), " — The member can enter up to a set number of times."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Unlimited entries"
          }), " — The member can enter any number of times."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Set a ", _jsx(_components.strong, {
          children: "Re-entry time delay"
        }), " to prevent members from re-entering too soon after their previous exit."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-start-reentry.png",
          alt: "Re-entry options"
        })
      }), "\n", _jsx(_components.h4, {
        id: "simultaneous-entry",
        children: "Simultaneous entry"
      }), "\n", _jsx(_components.p, {
        children: "For event-based journeys that allow multiple entries, simultaneous entry controls whether the same member can have more than one active entry at the same time. You can prevent simultaneous entries, set a limit, or allow an unlimited number."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The re-entry delay applies only when the member isn't currently in the\njourney. If the member is already participating, the simultaneous-entry\nsetting determines whether another event can create an additional entry."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-start-sim-entry.png",
          alt: "Simultaneous entry options"
        })
      }), "\n", _jsx(_components.h4, {
        id: "exclusions",
        children: "Exclusions"
      }), "\n", _jsx(_components.p, {
        children: "Exclusions prevent a member from entering this journey while they're already in another journey on the same parent model:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Don't exclude any profiles"
          }), " ", _jsx(_components.em, {
            children: "(default)"
          }), " — Members can participate in multiple journeys at the same time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Exclude profiles who are currently in any other journey"
          }), " — Prevents entry while the member is in any other journey."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Exclude profiles who are currently in specific journey(s)"
          }), " — Prevents entry while the member is in a journey you select."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Exclusions are checked when a member enters. They don't remove members who are already in progress."
      }), "\n", _jsxs(_components.p, {
        children: ["If the entry audience belongs to a ", _jsx(_components.a, {
          href: "/customer-studio/priority-lists",
          children: "priority list"
        }), ", audience priority can also affect whether a member qualifies to enter."]
      }), "\n", _jsx(_components.h4, {
        id: "subsets",
        children: "Subsets"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "Subsets"
        }), " restrict which rows of the parent model a journey can use. Where a journey gets its subsets depends on the entry type:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Enters audience"
          }), " — The journey uses the entry audience's subsets. There is nothing to configure on the Start tile."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Performs events"
          }), " — Events aren't scoped by an audience, so you select subsets on the Start tile. The ", _jsx(_components.strong, {
            children: "Subsets"
          }), " card lists every subset category on the parent model. A category marked ", _jsx(_components.strong, {
            children: "optional"
          }), " can stay empty. A required category must have a selection before you can select ", _jsx(_components.strong, {
            children: "Update"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journey-start-event-subsets.png",
          alt: "Subsets card on an event-based Start tile, with North America selected for the required Region category and Gold and Silver selected for the optional Tier category"
        })
      }), "\n", _jsx(_components.p, {
        children: "Within a category, a member must match any selected subset. Across categories, a member must match every category that has a selection. This is the same rule the audience builder applies. Hightouch applies the selection when a member enters the journey. Members already in the journey aren't removed if they stop matching."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Send email"
        }), ", ", _jsx(_components.strong, {
          children: "Send SMS"
        }), ", and ", _jsx(_components.strong, {
          children: "Send push"
        }), " tiles check the same selection again when they send, so a member who stops matching isn't sent those messages. See ", _jsx(_components.a, {
          href: "/native-delivery/journeys#subsets-on-the-parent-model",
          children: "Send from a journey"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You can only select subsets your user groups have access to. Subsets you can't use appear disabled. If a required category has exactly one subset you can use, Hightouch selects it for you."
      }), "\n", _jsxs(_components.p, {
        children: ["If a subset selected on the Start tile is deleted, the journey's runs fail, so members the subset excluded can't enter. To fix it, open the Start tile in edit mode and select ", _jsx(_components.strong, {
          children: "Remove deleted subset"
        }), ". You can't update the tile until you do. If removing it leaves a required category empty, select a new subset for that category. If it leaves an optional category empty, members the deleted subset excluded can enter again."]
      }), "\n", _jsx(_components.h2, {
        id: "3-add-journey-tiles",
        children: "3. Add journey tiles"
      }), "\n", _jsxs(_components.p, {
        children: ["Add the tiles members should move through after they enter. Drag a tile from the ", _jsx(_components.strong, {
          children: "Add to canvas"
        }), " panel, or select the ", _jsx(_components.code, {
          children: "+"
        }), " below an existing tile."]
      }), "\n", _jsx(_components.p, {
        children: "Journey tiles can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Control timing"
          }), " — Time delay, Hold until"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create different paths"
          }), " — Segment, A/B Split"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Send messages or data"
          }), " — Send email, Send SMS, Send push, Send to destination"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Carry or assign information"
          }), " — Set variable, Assign coupon"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Configure each tile in the order members should encounter it. See ", _jsx(_components.a, {
          href: "/customer-studio/journeys/tiles",
          children: "Journey tiles"
        }), " for configuration and evaluation behavior."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journey-canvas-tile-library.gif",
          alt: "Journey canvas with tile library panel"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Drag empty space to pan around the canvas, and use the ", _jsx(_components.code, {
          children: "+"
        }), " and ", _jsx(_components.code, {
          children: "-"
        }), " controls to zoom. Use the controls on a tile to move, duplicate, or delete it, and hold ", _jsx(_components.code, {
          children: "Shift"
        }), " to select multiple tiles. Use the clean-up control to tidy the layout. You can't delete the ", _jsx(_components.strong, {
          children: "Start"
        }), " tile."]
      }), "\n", _jsx(_components.h3, {
        id: "add-journey-notes",
        children: "Add journey notes"
      }), "\n", _jsx(_components.p, {
        children: "Add notes to record context about the journey or a specific tile directly on the canvas."
      }), "\n", _jsx(_components.p, {
        children: "You can add a note by:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Dragging a note from the tile library onto the canvas or a tile.\n", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/journey-canvas-note.png",
            alt: "Journey canvas note"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Adding a note directly to a tile.\n", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/journey-note-direct-tile.png",
            alt: "Journey direct tile"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Opening a tile and adding a note from its configuration.\n", _jsx(_components.img, {
            src: "hightouch-audiences/journeys/journey-note-drawer.png",
            alt: "Adding a note to a tile"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Notes are copied when you create a journey from a ", _jsx(_components.a, {
          href: "/customer-studio/templates#journey-templates",
          children: "template"
        }), ". Copied notes are attributed to the person who creates the journey."]
      }), "\n", _jsx(_components.h2, {
        id: "4-set-exit-criteria",
        children: "4. Set exit criteria"
      }), "\n", _jsx(_components.p, {
        children: "Exit criteria remove members when a condition becomes true, such as making a purchase, unsubscribing, or changing audience membership. Configure them in the journey settings."
      }), "\n", _jsx(_components.p, {
        children: "On each scheduled run, Hightouch checks exit criteria before advancing members. If a condition is met, the member exits before later tiles can send a message or sync data. If the condition becomes true between runs, the member remains in the journey until the next run detects it."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-settings-exit.png",
          alt: "Exit criteria tab"
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Use exit conditions that are unlikely to become false again. For example, if\nmembers enter because they haven't purchased in 30 days, a purchase is a clear\nexit condition. Avoid exit conditions based on values that can repeatedly\nchange, because members may leave and later qualify to enter again."
        })
      }), "\n", _jsx(_components.h2, {
        id: "5-set-the-schedule",
        children: "5. Set the schedule"
      }), "\n", _jsx(_components.p, {
        children: "The journey schedule determines how often Hightouch checks for new entries, evaluates exit criteria, advances members, and runs destination syncs."
      }), "\n", _jsx(_components.p, {
        children: "Choose a schedule based on the timing precision your journey requires:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Schedule type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Example"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Interval"
              })
            }), _jsx(_components.td, {
              children: "Runs at a repeating interval"
            }), _jsx(_components.td, {
              children: "Every hour or every 30 minutes"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom recurrence"
              })
            }), _jsx(_components.td, {
              children: "Runs on specified days and times"
            }), _jsx(_components.td, {
              children: "Weekdays at 9 AM"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "For example, an hourly schedule may be appropriate for cart abandonment, while a daily schedule may be enough for a winback journey."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-schedule.png",
          alt: "Evaluation schedule settings"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Members only advance when the journey runs. On a daily schedule, a one-minute\ndelay and a one-day delay can both resolve on the next daily run. More\nfrequent schedules evaluate delays more precisely but use more warehouse\nqueries. See ", _jsx(_components.a, {
            href: "/customer-studio/journeys#how-the-schedule-affects-timing",
            children: "How the schedule affects\ntiming"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "6-test-and-activate",
        children: "6. Test and activate"
      }), "\n", _jsx(_components.p, {
        children: "After configuring the journey, validate its logic, test destination actions, and run it with a small live cohort before launching it broadly."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/customer-studio/journeys/activate",
          children: "Test and launch a journey"
        }), " for the complete validation and launch process."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "examples",
        children: "Examples"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journey-example-rewards.png",
          alt: "A journey on the canvas: audience entry, an email tile, and a Hold until branch based on a purchase event"
        })
      }), "\n", _jsx(_components.h3, {
        id: "welcome-series",
        children: "Welcome series"
      }), "\n", _jsx(_components.p, {
        children: "A new member signs up, receives a welcome email, and receives a reminder only if they haven't activated."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Entry:"
          }), " Event-based (", _jsx(_components.code, {
            children: "signed_up"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Schedule:"
          }), " Hourly"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Flow:"
          }), " Start → welcome email → three-day delay → Hold until first action → reminder if the action doesn't occur → End"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Exit:"
          }), " First purchase or another key activation event"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "cart-abandonment",
        children: "Cart abandonment"
      }), "\n", _jsx(_components.p, {
        children: "A member abandons a cart and receives a sequence that stops when they purchase."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Entry:"
          }), " Event-based (", _jsx(_components.code, {
            children: "cart_abandoned"
          }), "), or audience-based if abandonment is represented as a state"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Schedule:"
          }), " Hourly"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Flow:"
          }), " Start → two-hour delay → Send email → one-day delay → Segment by SMS eligibility → Send SMS → End"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Exit:"
          }), " Purchase event"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "re-engagement",
        children: "Re-engagement"
      }), "\n", _jsx(_components.p, {
        children: "A member who has been inactive for 60 days receives a three-message sequence and leaves when they re-engage."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Entry:"
          }), " Audience-based (", _jsx(_components.code, {
            children: "inactive 60+ days"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Schedule:"
          }), " Daily"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Flow:"
          }), " Start → email 1 → four-day delay → email 2 → seven-day delay → final email → End"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Exit:"
          }), " Purchase, login, or opt-out"]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/journeys/tiles",
            children: "Journey tiles"
          }), " to configure delays, branches, messages, destination syncs, variables, and coupons."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/journeys/activate",
            children: "Test and launch a journey"
          }), " to validate the journey before members are affected."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/templates#journey-templates",
            children: "Journey templates"
          }), " to reuse a journey structure."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
